import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { PiCheckCircle, PiWarningCircle } from "react-icons/pi";

import useUiContext from "@/custolink-hooks/useUiContext";
import useFormServerBacked from "@/custolink-hooks/form/useFormServerBacked";
import useFormErrorPicker from "@/custolink-hooks/form/useFormErrorPicker";
import {
  PublicSharedUserApi,
  type PublicSharedUserApi$VerifyEmailAndSetPasswordInput,
} from "@/custolink-transpiled_by_server/bc.identity_and_access.api_methods";
import { normaliseHex } from "@/custolink-views/asset_frontend/redesigns/theme";
import { usePreviewFonts } from "@/custolink-views/asset_frontend/redesigns/fonts";
import { AssetShell, PreviewHeader } from "@/custolink-views/asset_frontend/redesigns/tileKit";
import {
  ErrorNote,
  FormField,
  InfoNote,
  PasswordField,
  PrimaryButton,
} from "@/custolink-views/asset_frontend/redesigns/formKit";

/**
 * Verify email for an end user, asset frontend design.
 *
 * Behaviour is VerifyEmailSharedForm's, unchanged: same
 * getVerifyEmailTokenInfo query, same branch on mustSetPassword into an
 * auto-confirm or a set-password form, same verifyEmailAndSetPassword
 * mutation. This page does not reuse VerifyEmailSharedForm because that
 * component carries the classic Loading/Alert/Input/Button/FormItem markup -
 * only the presentation moves, onto tileKit.AssetShell / formKit.
 */
export default function VerifyEmailForEndUserDesign() {
  const { token } = useParams();
  const uic = useUiContext();
  usePreviewFonts(["schibsted"]);

  const lf = uic.organisationContext.lookAndFeel;
  const accent = normaliseHex(lf.assetFrontendBgcolorHex);

  return (
    <AssetShell accent={accent}>
      <PreviewHeader logoUrl={lf.logoUrl} />

      <div className="flex flex-col gap-3 px-4">
        <div className="rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] px-4 py-4 sm:py-5 text-[var(--rda-on-surface)]">
          <h1 className="text-lg font-semibold tracking-[-0.01em] sm:text-xl">Welcome</h1>
          <div className="mt-4">
            <VerifyEmailBody token={token as string} />
          </div>
        </div>
      </div>
    </AssetShell>
  );
}

function VerifyEmailBody(p: { token: string }) {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mode, setMode] = useState<"initial" | "confirm" | "set_password">("initial");

  const u = useQuery({
    queryFn: () => PublicSharedUserApi.getVerifyEmailTokenInfo(p.token),
    queryKey: ["initialConfirmInvitationToken", p.token],
  });

  useEffect(() => {
    if (u.data != null) {
      if (u.data.ok) {
        setMode(u.data.mustSetPassword ? "set_password" : "confirm");
      } else {
        setErrorMessage(u.data.errorMessageIfNotOk);
      }
    }
  }, [u.data]);

  if (u.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--rda-muted)]">
        <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-50" />
        Checking your link...
      </div>
    );
  }

  if (errorMessage != null) {
    return (
      <div className="flex flex-col gap-4">
        <ErrorNote>
          <span className="flex items-center gap-2">
            <PiWarningCircle aria-hidden className="text-lg" />
            {errorMessage}
          </span>
        </ErrorNote>
        <PrimaryButton type="button" onClick={() => navigate("/")}>
          Continue
        </PrimaryButton>
      </div>
    );
  }

  return (
    <>
      {mode == "confirm" && <ConfirmNow token={p.token} />}
      {mode == "set_password" && <SetPasswordForm token={p.token} />}
    </>
  );
}

function VerifiedThankYou() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-4">
      <InfoNote>
        <span className="flex items-center gap-2">
          <PiCheckCircle aria-hidden className="text-lg" />
          Your email address has verified. From now on, please use this email to sign in.
          All future communications will be sent to this address.
        </span>
      </InfoNote>
      <PrimaryButton type="button" onClick={() => navigate("/")}>
        Continue
      </PrimaryButton>
    </div>
  );
}

function ConfirmNow(p: { token: string }) {
  const [done, setDone] = useState(false);
  const m = useMutation({
    mutationFn: (token: string) =>
      PublicSharedUserApi.verifyEmailAndSetPassword({
        token: token,
        newPassword: null,
      }),
    mutationKey: ["verifyEmailAndSetPassword"],
  });

  useEffect(() => {
    m.mutateAsync(p.token)
      .then(() => setDone(true))
      .catch((err) => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (done) {
    return <VerifiedThankYou />;
  }

  return (
    <div className="flex items-center gap-2 text-sm text-[var(--rda-muted)]">
      <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-50" />
      Verifying your email...
    </div>
  );
}

function SetPasswordForm(p: { token: string }) {
  const [repeat, setRepeat] = useState("");
  const [repeatError, setRepeatError] = useState(false);
  const [done, setDone] = useState(false);

  // This page is entirely customer-facing, so the error toast follows the asset-frontend
  // switch, not the admin one.
  const form = useFormServerBacked<PublicSharedUserApi$VerifyEmailAndSetPasswordInput>(
    {
      token: p.token,
      newPassword: "",
    },
    (d) => {
      if (d.newPassword != repeat) {
        setRepeatError(true);
        return Promise.reject();
      } else {
        setRepeatError(false);
      }
      return PublicSharedUserApi.verifyEmailAndSetPassword(d);
    },
    () => {
      form.reset({ newPassword: "" });
      setRepeat("");
      setDone(true);
    },
    undefined,
    "asset",
  );

  const e = useFormErrorPicker(form.errors, "newPassword");

  if (done) {
    return <VerifiedThankYou />;
  }

  return (
    <form noValidate className="flex flex-col gap-1" onSubmit={form.handleSubmit()}>
      <p className="text-sm text-[var(--rda-muted)]">
        To continue, please choose a password for your account, and submit below.
      </p>

      <FormField required label="New password" error={e.newPassword}>
        <PasswordField {...form.register("newPassword")} autoComplete="off" />
      </FormField>

      <FormField
        required
        label="Repeat new password"
        error={repeatError ? "The new password does not match this one." : undefined}
      >
        <PasswordField
          autoComplete="off"
          value={repeat}
          onChange={(ev) => setRepeat(ev.target.value)}
        />
      </FormField>

      <PrimaryButton className="mt-3" type="submit" disabled={form.isSubmitting}>
        {form.isSubmitting ? "Saving..." : "Save"}
      </PrimaryButton>
    </form>
  );
}
